/**
 * Minimal Jinja2 renderer for reviewing protocol step templates.
 *
 * Supported syntax:
 *   {{ expr }}                              – variable output (dot notation, ternary)
 *   {% if expr %}...{% elif %}...{% else %}...{% endif %}  – conditionals
 *   {% for x in expr %}...{% endfor %}      – array iteration
 *   {% for k, v in expr.items() %}...{% endfor %} – dict iteration
 *
 * Supported operators, loosest binding first:
 *   or, and, not, comparisons (==, !=, >, <, >=, <=), then pipe filters.
 *
 * Supported filters (pipe syntax):
 *   {{ list|length }}                       – array/object length
 *   {{ list|map(attribute='name') }}        – project one attribute of every item
 *   {{ list|join(', ') }}                   – join an array into a string
 *   {% for x in list|select('equalto', y) %}   – keep items passing a test
 *   {% for x in list|reject('equalto', y) %}   – drop items passing a test
 *   {% for x in list|selectattr('field') %} – keep items where field is truthy
 *   {% for x in list|selectattr('a.b', 'equalto', 'cil') %} – attribute + test
 *   {% for x in list|rejectattr('field') %} – keep items where field is falsy
 *   {{ file_ref|media }}                    – file-reference dict to <x-media> element
 *
 * Attribute arguments accept dotted paths (`data.code`), like real Jinja2.
 */

type Scope = Record<string, unknown>

// ── public API ──────────────────────────────────────────────────────

export function renderJinja(template: string, context: Scope): string {
  const rootScope: Scope = { context, ...context }
  const tokens = tokenize(template)
  const ast = parse(tokens)
  return renderNodes(ast, rootScope).trim()
}

// ── tokenizer ───────────────────────────────────────────────────────

interface TextToken {
  type: 'text'
  value: string
}
interface VarToken {
  type: 'var'
  expr: string
}
interface TagToken {
  type: 'tag'
  tag: string
}

type Token = TextToken | VarToken | TagToken

function tokenize(template: string): Token[] {
  const tokens: Token[] = []
  const re = /(\{%.*?%\}|\{\{.*?\}\})/gs
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = re.exec(template)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: 'text', value: template.slice(lastIndex, match.index) })
    }
    const raw = match[0]
    if (raw.startsWith('{{')) {
      tokens.push({ type: 'var', expr: raw.slice(2, -2).trim() })
    } else {
      tokens.push({ type: 'tag', tag: raw.slice(2, -2).trim() })
    }
    lastIndex = re.lastIndex
  }

  if (lastIndex < template.length) {
    tokens.push({ type: 'text', value: template.slice(lastIndex) })
  }
  return tokens
}

// ── AST ─────────────────────────────────────────────────────────────

interface TextNode {
  kind: 'text'
  value: string
}
interface VarNode {
  kind: 'var'
  expr: string
}
interface IfNode {
  kind: 'if'
  branches: { condition: string | null; body: AstNode[] }[]
}
interface ForNode {
  kind: 'for'
  varNames: string[]
  iterExpr: string
  isItems: boolean
  body: AstNode[]
}

type AstNode = TextNode | VarNode | IfNode | ForNode

function parse(tokens: Token[], stopTags?: string[]): AstNode[] {
  const nodes: AstNode[] = []

  while (tokens.length > 0) {
    const token = tokens[0]

    if (token.type === 'text') {
      tokens.shift()
      nodes.push({ kind: 'text', value: token.value })
      continue
    }

    if (token.type === 'var') {
      tokens.shift()
      nodes.push({ kind: 'var', expr: token.expr })
      continue
    }

    // tag
    const tag = token.tag

    if (stopTags && stopTags.some((s) => tag === s || tag.startsWith(s + ' '))) {
      break
    }

    tokens.shift()

    if (tag.startsWith('if ')) {
      nodes.push(parseIf(tag.slice(3).trim(), tokens))
    } else if (tag.startsWith('for ')) {
      nodes.push(parseFor(tag.slice(4).trim(), tokens))
    }
    // skip unknown block tags
  }

  return nodes
}

function parseIf(condition: string, tokens: Token[]): IfNode {
  const branches: IfNode['branches'] = []

  let body = parse(tokens, ['elif', 'else', 'endif'])
  branches.push({ condition, body })

  while (tokens.length > 0) {
    const tag = (tokens[0] as TagToken).tag
    tokens.shift()

    if (tag === 'endif') {
      break
    }
    if (tag.startsWith('elif ')) {
      body = parse(tokens, ['elif', 'else', 'endif'])
      branches.push({ condition: tag.slice(5).trim(), body })
    } else if (tag === 'else') {
      body = parse(tokens, ['endif'])
      branches.push({ condition: null, body })
    }
  }

  return { kind: 'if', branches }
}

function parseFor(header: string, tokens: Token[]): ForNode {
  // "x in expr" or "k, v in expr.items()"
  const inMatch = header.match(/^(.+?)\s+in\s+(.+)$/)
  if (!inMatch) {
    // skip to endfor
    parse(tokens, ['endfor'])
    if (tokens.length > 0) tokens.shift()
    return { kind: 'for', varNames: [], iterExpr: '', isItems: false, body: [] }
  }

  const varPart = inMatch[1].trim()
  let iterExpr = inMatch[2].trim()
  const isItems = iterExpr.endsWith('.items()')
  if (isItems) {
    iterExpr = iterExpr.slice(0, -8)
  }

  const varNames = varPart.split(',').map((v) => v.trim())
  const body = parse(tokens, ['endfor'])
  if (tokens.length > 0) tokens.shift() // consume endfor

  return { kind: 'for', varNames, iterExpr, isItems, body }
}

// ── renderer ────────────────────────────────────────────────────────

function renderNodes(nodes: AstNode[], scope: Scope): string {
  let result = ''
  for (const node of nodes) {
    switch (node.kind) {
      case 'text':
        result += node.value
        break
      case 'var':
        result += evalExpression(node.expr, scope)
        break
      case 'if':
        result += renderIf(node, scope)
        break
      case 'for':
        result += renderFor(node, scope)
        break
    }
  }
  return result
}

function renderIf(node: IfNode, scope: Scope): string {
  for (const branch of node.branches) {
    if (branch.condition === null || isTruthy(resolveValue(branch.condition, scope))) {
      return renderNodes(branch.body, scope)
    }
  }
  return ''
}

function renderFor(node: ForNode, scope: Scope): string {
  const iterable = resolveValue(node.iterExpr, scope)
  if (iterable == null) return ''

  let result = ''

  if (node.isItems && typeof iterable === 'object' && !Array.isArray(iterable)) {
    const entries = Object.entries(iterable as Record<string, unknown>)
    for (const [key, value] of entries) {
      const innerScope: Scope = { ...scope }
      if (node.varNames.length >= 2) {
        innerScope[node.varNames[0]] = key
        innerScope[node.varNames[1]] = value
      } else if (node.varNames.length === 1) {
        innerScope[node.varNames[0]] = [key, value]
      }
      result += renderNodes(node.body, innerScope)
    }
  } else if (Array.isArray(iterable)) {
    for (const item of iterable) {
      const innerScope: Scope = { ...scope }
      if (node.varNames.length === 1) {
        innerScope[node.varNames[0]] = item
      }
      result += renderNodes(node.body, innerScope)
    }
  }

  return result
}

// ── expression evaluation ───────────────────────────────────────────

function evalExpression(expr: string, scope: Scope): string {
  // Handle ternary: "a if b else c"
  const ternaryMatch = expr.match(/^(.+?)\s+if\s+(.+?)\s+else\s+(.+)$/)
  if (ternaryMatch) {
    const condition = resolveValue(ternaryMatch[2].trim(), scope)
    if (isTruthy(condition)) {
      return stringify(resolveValue(ternaryMatch[1].trim(), scope))
    }
    return stringify(resolveValue(ternaryMatch[3].trim(), scope))
  }

  const value = resolveValue(expr, scope)
  return stringify(value)
}

/**
 * Evaluate an expression, lowest-binding operator first.
 *
 * Precedence follows Jinja2: `or` < `and` < `not` < comparisons < filters.
 * Splitting on filters first (the pre-#931 order) made
 * `xs and xs | length > 1` parse as `(xs and xs) | length`, silently dropping
 * the `> 1` and leaving the condition permanently true.
 */
function resolveValue(expr: string, scope: Scope): unknown {
  const trimmed = expr.trim()

  // A fully parenthesised expression: "(a or b)" -> "a or b".
  const unwrapped = unwrapParens(trimmed)
  if (unwrapped !== null) {
    return resolveValue(unwrapped, scope)
  }

  // "a or b"
  const orParts = splitLogical(trimmed, ' or ')
  if (orParts) {
    const left = resolveValue(orParts[0], scope)
    if (isTruthy(left)) return left
    return resolveValue(orParts[1], scope)
  }

  // "a and b"
  const andParts = splitLogical(trimmed, ' and ')
  if (andParts) {
    const left = resolveValue(andParts[0], scope)
    if (!isTruthy(left)) return left
    return resolveValue(andParts[1], scope)
  }

  // "not expr": binds looser than a comparison, so "not a == b" is "not (a == b)".
  if (trimmed.startsWith('not ')) {
    return !isTruthy(resolveValue(trimmed.slice(4).trim(), scope))
  }

  // "a > b", "a == b" and friends: bind tighter than and/or, looser than filters.
  const comparison = splitComparison(trimmed)
  if (comparison) {
    return compareValues(
      resolveValue(comparison.left, scope),
      comparison.operator,
      resolveValue(comparison.right, scope)
    )
  }

  // Handle pipe filters: "expr|filter1('arg')|filter2"
  const filterResult = splitFilters(trimmed)
  if (filterResult) {
    let value = resolveValue(filterResult.base, scope)
    for (const filter of filterResult.filters) {
      value = applyFilter(value, filter, scope)
    }
    return value
  }

  // String literal: 'xxx' or "xxx"
  const strMatch = trimmed.match(/^['"](.*)['"]$/)
  if (strMatch) {
    return strMatch[1]
  }

  // Number literal
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return Number(trimmed)
  }

  // Boolean / None literals
  if (trimmed === 'true' || trimmed === 'True') return true
  if (trimmed === 'false' || trimmed === 'False') return false
  if (trimmed === 'none' || trimmed === 'None') return null

  // Dot path: context.xxx.yyy
  return resolvePath(trimmed, scope)
}

const COMPARISON_OPERATORS = ['==', '!=', '>=', '<=', '>', '<'] as const

type ComparisonOperator = (typeof COMPARISON_OPERATORS)[number]

/**
 * Index of the first occurrence of any needle that sits at bracket depth 0 and
 * outside a quoted string. Keeps `join(' > ')` and `select('equalto', x)` from
 * being mistaken for operators.
 */
function findTopLevel(
  expr: string,
  needles: readonly string[]
): { index: number; needle: string } | null {
  let depth = 0
  let quote: string | null = null

  for (let i = 0; i < expr.length; i += 1) {
    const char = expr[i]

    if (quote) {
      if (char === quote) quote = null
      continue
    }
    if (char === "'" || char === '"') {
      quote = char
      continue
    }
    if (char === '(' || char === '[') {
      depth += 1
      continue
    }
    if (char === ')' || char === ']') {
      depth -= 1
      continue
    }
    if (depth !== 0) continue

    for (const needle of needles) {
      if (expr.startsWith(needle, i)) {
        return { index: i, needle }
      }
    }
  }

  return null
}

/** Strip one layer of parentheses when they wrap the whole expression. */
function unwrapParens(expr: string): string | null {
  if (!expr.startsWith('(') || !expr.endsWith(')')) return null

  let depth = 0
  let quote: string | null = null

  for (let i = 0; i < expr.length; i += 1) {
    const char = expr[i]
    if (quote) {
      if (char === quote) quote = null
      continue
    }
    if (char === "'" || char === '"') {
      quote = char
      continue
    }
    if (char === '(' || char === '[') depth += 1
    if (char === ')' || char === ']') {
      depth -= 1
      // The opening paren closes before the end: "(a) and (b)" is not wrapped.
      if (depth === 0 && i !== expr.length - 1) return null
    }
  }

  return depth === 0 ? expr.slice(1, -1).trim() : null
}

function splitComparison(
  expr: string
): { left: string; operator: ComparisonOperator; right: string } | null {
  const found = findTopLevel(expr, COMPARISON_OPERATORS)
  if (!found) return null

  const left = expr.slice(0, found.index).trim()
  const right = expr.slice(found.index + found.needle.length).trim()
  // "= 1" or "x >" is not a comparison; leave it to the path resolver rather
  // than evaluating half an expression.
  if (!left || !right) return null

  return { left, operator: found.needle as ComparisonOperator, right }
}

/**
 * Compare like Jinja2 does for the shapes protocol templates produce: numbers
 * when both sides read as numbers (`list|length > 1`), lexicographic strings
 * when both sides are genuinely strings. Equality reuses `jinjaEquals`, which
 * already tolerates the number/string mixing the templates rely on.
 *
 * Real Jinja2 raises a TypeError when the operands are unorderable (a dict
 * against a number, an undefined path, a list against a string). This renderer
 * runs in front of a factory operator, so it must never throw mid-render: an
 * unorderable pair returns false for every ordering operator instead. That is a
 * defined, deliberately falsy result, so a template comparing incomparable
 * values takes its `{% else %}` branch rather than a plausible-looking but
 * wrong one produced by coercing both sides to strings.
 */
function compareValues(left: unknown, operator: ComparisonOperator, right: unknown): boolean {
  if (operator === '==') return jinjaEquals(left, right)
  if (operator === '!=') return !jinjaEquals(left, right)

  const operands = orderableOperands(left, right)
  // Unorderable: neither a numeric pair nor a string pair. Jinja2 would raise;
  // we degrade to false so both `a > b` and `a <= b` read as "not true".
  if (!operands) return false

  const [a, b] = operands
  switch (operator) {
    case '>':
      return a > b
    case '<':
      return a < b
    case '>=':
      return a >= b
    case '<=':
      return a <= b
  }
}

/**
 * Reduce a pair to comparable operands, or null when Jinja2 would refuse to
 * order them. Numbers win over strings so `'2' > 1` still works, matching the
 * number/string mixing the protocol templates rely on.
 */
function orderableOperands(
  left: unknown,
  right: unknown
): [number, number] | [string, string] | null {
  const leftNumber = toComparableNumber(left)
  const rightNumber = toComparableNumber(right)
  if (leftNumber !== null && rightNumber !== null) return [leftNumber, rightNumber]

  // Only genuine strings compare lexicographically. Objects, arrays, booleans,
  // null/undefined and mixed number/non-numeric-string pairs are unorderable.
  if (typeof left === 'string' && typeof right === 'string') return [left, right]

  return null
}

function toComparableNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

interface FilterCall {
  name: string
  /** Positional arguments, kept as raw expressions so they can be resolved
   *  against the scope (a test operand may be `context.failure_mode`). */
  args: string[]
  /** Keyword arguments (`attribute='name'`), same raw-expression treatment. */
  kwargs: Record<string, string>
}

/** Split a filter's argument list on top-level commas, leaving quoted strings
 *  and nested parens/brackets intact. */
function splitArgs(raw: string): string[] {
  const parts: string[] = []
  let depth = 0
  let quote: string | null = null
  let current = ''

  for (const char of raw) {
    if (quote) {
      current += char
      if (char === quote) quote = null
      continue
    }
    if (char === "'" || char === '"') {
      quote = char
      current += char
      continue
    }
    if (char === '(' || char === '[') depth++
    if (char === ')' || char === ']') depth--
    if (char === ',' && depth === 0) {
      parts.push(current.trim())
      current = ''
      continue
    }
    current += char
  }
  parts.push(current.trim())
  return parts.filter((part) => part.length > 0)
}

function parseFilterArgs(raw: string | undefined): Pick<FilterCall, 'args' | 'kwargs'> {
  const args: string[] = []
  const kwargs: Record<string, string> = {}
  if (!raw) return { args, kwargs }

  for (const part of splitArgs(raw)) {
    // `name=value`, but not the `==` of a comparison.
    const kwarg = part.match(/^([A-Za-z_]\w*)\s*=(?!=)\s*([\s\S]+)$/)
    if (kwarg) {
      kwargs[kwarg[1]] = kwarg[2].trim()
    } else {
      args.push(part)
    }
  }
  return { args, kwargs }
}

function splitFilters(expr: string): { base: string; filters: FilterCall[] } | null {
  // Split on | that is followed by a filter name (not inside quotes).
  // Jinja allows whitespace around the pipe ("x | media"), so tolerate it.
  // The argument list accepts anything except unquoted parens, so kwargs
  // (`attribute='name'`), bare identifiers and multiple positional arguments
  // all survive: the previous regex only matched a single quoted argument and
  // silently dropped the whole call otherwise.
  const filterRe = /\s*\|\s*(\w+)\s*(?:\(((?:[^()'"]|'[^']*'|"[^"]*")*)\))?/g
  let firstIndex = -1
  let match: RegExpExecArray | null
  const filters: FilterCall[] = []

  while ((match = filterRe.exec(expr)) !== null) {
    if (firstIndex === -1) firstIndex = match.index
    filters.push({ name: match[1], ...parseFilterArgs(match[2]) })
  }

  if (filters.length === 0) return null
  return { base: expr.slice(0, firstIndex).trim(), filters }
}

/** Jinja compares with `==`; templates routinely mix a numeric context value
 *  with a string literal, so scalars are compared on their text form. */
function jinjaEquals(left: unknown, right: unknown): boolean {
  if (left === right) return true
  if (left == null || right == null) return false
  if (typeof left === 'object' || typeof right === 'object') return false
  return String(left) === String(right)
}

/** The handful of Jinja tests the protocol templates use. An unknown test name
 *  degrades to a truthiness check rather than dropping the whole filter. */
function applyTest(item: unknown, test: string | null, operand: unknown): boolean {
  switch (test) {
    case null:
      return isTruthy(item)
    case 'equalto':
    case 'eq':
    case '==':
      return jinjaEquals(item, operand)
    case 'ne':
    case '!=':
      return !jinjaEquals(item, operand)
    case 'defined':
      return item !== undefined
    case 'undefined':
      return item === undefined
    case 'none':
      return item === null
    case 'in':
      return Array.isArray(operand) ? operand.some((entry) => jinjaEquals(item, entry)) : false
    default:
      return isTruthy(item)
  }
}

/** Shared body of select/reject and selectattr/rejectattr. `attribute` is a
 *  dotted path applied to each item first; null tests the item itself. */
function filterByTest(
  value: unknown,
  filter: FilterCall,
  scope: Scope,
  attribute: string | null,
  keep: boolean
): unknown {
  if (!Array.isArray(value)) return value

  const testName = filter.args.length ? String(resolveValue(filter.args[0], scope) ?? '') : null
  const operand = filter.args.length > 1 ? resolveValue(filter.args[1], scope) : undefined

  return value.filter((item) => {
    const subject = attribute ? resolvePath(attribute, item as Scope) : item
    return applyTest(subject, testName, operand) === keep
  })
}

/** The attribute a select/reject-attr or map call projects, as a dotted path. */
function filterAttribute(filter: FilterCall, scope: Scope, positionalIndex: number): string | null {
  const kwarg = filter.kwargs['attribute']
  if (kwarg !== undefined) {
    const resolved = resolveValue(kwarg, scope)
    return resolved == null ? null : String(resolved)
  }
  const positional = filter.args[positionalIndex]
  if (positional === undefined) return null
  const resolved = resolveValue(positional, scope)
  return resolved == null ? null : String(resolved)
}

function applyFilter(value: unknown, filter: FilterCall, scope: Scope): unknown {
  switch (filter.name) {
    case 'length':
      if (Array.isArray(value)) return value.length
      if (typeof value === 'string') return value.length
      if (value && typeof value === 'object') return Object.keys(value as object).length
      return 0

    case 'list':
      if (Array.isArray(value)) return value
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return Object.keys(value as object)
      }
      return value == null ? [] : [value]

    case 'map': {
      // Only the `map(attribute=…)` form is supported: `map('filtername')`
      // would need the filter registry to be re-entrant per item, and no
      // protocol template uses it.
      const attribute = filterAttribute(filter, scope, 0)
      if (!Array.isArray(value) || !attribute) return value
      return value.map((item) => resolvePath(attribute, item as Scope))
    }

    case 'join': {
      if (!Array.isArray(value)) return value
      const separator = filter.args.length ? String(resolveValue(filter.args[0], scope) ?? '') : ''
      return value.map((item) => stringify(item)).join(separator)
    }

    case 'select':
      return filterByTest(value, filter, scope, null, true)

    case 'reject':
      return filterByTest(value, filter, scope, null, false)

    case 'selectattr': {
      const attribute = filterAttribute(filter, scope, 0)
      if (!attribute) return value
      return filterByTest(value, { ...filter, args: filter.args.slice(1) }, scope, attribute, true)
    }

    case 'rejectattr': {
      const attribute = filterAttribute(filter, scope, 0)
      if (!attribute) return value
      return filterByTest(value, { ...filter, args: filter.args.slice(1) }, scope, attribute, false)
    }

    case 'media': {
      // Mirror of the backend `media` filter (protocol/services/executor.py):
      // file-reference dict → <x-media> element, picked up by the x-media
      // content segmentation in session steps.
      if (!value || typeof value !== 'object' || Array.isArray(value)) return ''
      const ref = value as Record<string, unknown>
      const attr = (v: unknown) => String(v ?? '').replace(/"/g, '&quot;')
      return (
        `<x-media storage-key="${attr(ref['storage_key'])}"` +
        ` filename="${attr(ref['filename'])}"` +
        ` content-type="${attr(ref['content_type'])}"` +
        ` size="${attr(ref['size'] ?? 0)}"></x-media>`
      )
    }

    default:
      return value
  }
}

function resolvePath(path: string, scope: Scope): unknown {
  const parts = path.split('.')
  let current: unknown = scope

  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[part]
  }

  return current
}

function splitLogical(expr: string, operator: string): [string, string] | null {
  const found = findTopLevel(expr, [operator])
  if (!found) return null

  const left = expr.slice(0, found.index).trim()
  const right = expr.slice(found.index + operator.length).trim()
  if (!left || !right) return null
  return [left, right]
}

function isTruthy(value: unknown): boolean {
  if (value == null) return false
  if (value === false) return false
  if (value === 0) return false
  if (value === '') return false
  if (Array.isArray(value) && value.length === 0) return false
  if (typeof value === 'object' && Object.keys(value as object).length === 0) return false
  return true
}

/** Fields that carry the human-readable name of an entity dict, best first. A
 *  template that prints an entity wants its name, never `[object Object]`. */
const DISPLAY_NAME_FIELDS = ['name', 'label', 'title', 'display_name']

function stringify(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)

  // Arrays are stringified element-wise: letting one reach String() renders an
  // array of entity dicts as "[object Object],[object Object]".
  if (Array.isArray(value)) {
    return value
      .map((entry) => stringify(entry))
      .filter((entry) => entry !== '')
      .join(', ')
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    for (const field of DISPLAY_NAME_FIELDS) {
      const candidate = record[field]
      if (typeof candidate === 'string' && candidate) return candidate
    }
    return Object.entries(record)
      .map(([key, entry]) => `${key}: ${stringify(entry) || 'N/A'}`)
      .join(', ')
  }

  return String(value)
}
